<script setup lang="ts">
import { useIsMobile } from '~/composables/useIsMobile'
import { useAuthStore } from '~/store/auth'
import { getApiBase } from '~/utils/api-base'
import client from '~/api/client'
import {
  type AnnouncementItem,
  dismissAnnouncement,
  fetchAnnouncements,
  openAnnouncementCta,
} from '~/utils/announcements'

const props = withDefaults(
  defineProps<{
    sidebar?: boolean
    toolbar?: boolean
    condensed?: boolean
    horizontalScroll?: boolean
    fullHeight?: boolean
  }>(),
  {
    sidebar: true,
    toolbar: true,
    fullHeight: false,
  },
)

const collapsed = useState('wb-sidebar-collapsed', () => false)
const drawerOpen = useState('wb-drawer-open', () => false)
// „Chyba / nápad" from the mobile drawer (QA F055: the phone editor has no
// toolbar) — the modal lives here, the drawer that opens it unmounts on close.
const feedbackOpen = useState('wb-feedback-open', () => false)
const isMobile = useIsMobile()
const authStore = useAuthStore()
// Clients get the fixed 84px icon rail; admin keeps the collapsible text sidebar.
const isClient = computed(() => !authStore.user?.is_admin_role)
const sidebarWidth = computed(() =>
  isMobile.value ? '0' : isClient.value ? '84px' : collapsed.value ? '76px' : '248px',
)

const route = useRoute()
// web-edit ships its own mobile top bar (page switcher) + bottom nav (incl. a
// Nastavení tab that opens this drawer), so the global toolbar/hamburger is
// redundant there — hide it on mobile to leave only the page switcher.
const showToolbar = computed(() => props.toolbar && !(isMobile.value && route.path.startsWith('/web-edit')))

// ── e-mail verification banner (non-blocking — informational only) ───────────
// `email_verified` lands in the generated UserOut type after the next
// `pnpm generate-api`; read it loosely until then.
const VERIFY_BANNER_KEY = 'qcms-verify-banner-dismissed'
const verifyBannerDismissed = ref(
  typeof window !== 'undefined' && window.sessionStorage.getItem(VERIFY_BANNER_KEY) === '1',
)
const showVerifyBanner = computed(
  () =>
    !verifyBannerDismissed.value &&
    !!authStore.user &&
    (authStore.user as { email_verified?: boolean }).email_verified === false,
)
const resendState = ref<'idle' | 'sending' | 'sent' | 'error'>('idle')

// ── one-week app-rating prompt ─────────────────────────────────────────────
// Server decides (date_joined + 7 d, or the snoozed date; never admins, never
// after a rating). Checked once per app load, not per route.
const ratingPromptOpen = useState('qcms-rating-prompt', () => false)
const ratingPromptChecked = useState('qcms-rating-prompt-checked', () => false)

// ── Oznámení klientům (accounts.Announcement) ──────────────────────────────
// Loaded once per app load, only after the terms are accepted (terms modal
// first). Modal ones open one at a time; banner ones are strips above the
// content. A load that shows an announcement modal never also opens the rating
// prompt — two modals in a row read as spam.
const announcements = useState<AnnouncementItem[]>('qcms-announcements', () => [])
const announcementsLoaded = useState('qcms-announcements-loaded', () => false)
const hadAnnouncementModal = useState('qcms-announcement-modal-shown', () => false)
const modalAnnouncements = computed(() => announcements.value.filter((a) => a.display === 'modal'))
const bannerAnnouncements = computed(() => announcements.value.filter((a) => a.display !== 'modal'))
const currentAnnouncement = computed(() => (authStore.termsAccepted ? modalAnnouncements.value[0] : undefined))

function closeAnnouncement(a: AnnouncementItem) {
  announcements.value = announcements.value.filter((x) => x.id !== a.id)
  dismissAnnouncement(a.id)
}
function announcementCta(a: AnnouncementItem) {
  closeAnnouncement(a)
  openAnnouncementCta(a.cta_url)
}

// F084: a visit that showed the terms modal (accepted just now) is not the
// moment to ask for stars either — one modal per load.
const hadTermsModal = useState('qcms-terms-modal-shown', () => false)

onMounted(() => {
  watch(
    () => [authStore.user, authStore.termsAccepted] as const,
    async ([user, termsOk]) => {
      if (user && !termsOk) hadTermsModal.value = true
      if (!user || !termsOk) return
      if (!announcementsLoaded.value) {
        announcementsLoaded.value = true
        announcements.value = await fetchAnnouncements()
        if (modalAnnouncements.value.length) hadAnnouncementModal.value = true
      }
      if (ratingPromptChecked.value || user.is_admin_role) return
      ratingPromptChecked.value = true
      if (hadAnnouncementModal.value || hadTermsModal.value) return
      const { data } = await client.GET('/api/feedback/prompt/')
      if (data?.due)
        setTimeout(() => {
          if (!hadAnnouncementModal.value && !hadTermsModal.value && authStore.termsAccepted)
            ratingPromptOpen.value = true
        }, 4000)
    },
    { immediate: true },
  )
})

function dismissVerifyBanner() {
  verifyBannerDismissed.value = true
  window.sessionStorage.setItem(VERIFY_BANNER_KEY, '1')
}

async function resendVerification() {
  if (resendState.value === 'sending' || resendState.value === 'sent') return
  resendState.value = 'sending'
  try {
    const token = getAccessToken() || ''
    const res = await fetch(`${getApiBase()}/api/auth/resend-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    })
    resendState.value = res.ok ? 'sent' : 'error'
  } catch {
    resendState.value = 'error'
  }
}
</script>

<template>
  <div
    :style="
      fullHeight
        ? 'display: flex; height: 100dvh; overflow: hidden; background: var(--cream); color: var(--ink); font-family: var(--sans);'
        : 'display: flex; min-height: 100vh; background: var(--cream); color: var(--ink); font-family: var(--sans);'
    "
  >
    <!-- Desktop: static sidebar -->
    <slot name="sidebar">
      <SidebarNavigation v-if="sidebar && !isMobile" />
    </slot>

    <!-- Mobile: drawer overlay -->
    <template v-if="sidebar && isMobile">
      <div v-if="drawerOpen" class="wb-drawer-backdrop" @click="drawerOpen = false" />
      <div v-if="drawerOpen" class="wb-drawer">
        <SidebarNavigation :mobile="true" @navigate="drawerOpen = false" />
      </div>
    </template>

    <!-- Main content -->
    <div
      :style="[
        { marginLeft: sidebarWidth, transition: 'margin-left 200ms ease' },
        fullHeight
          ? { flex: '1', minWidth: '0', display: 'flex', flexDirection: 'column', height: '100%', minHeight: '0' }
          : { flex: '1', minWidth: '0', display: 'flex', flexDirection: 'column' },
      ]"
    >
      <slot name="toolbar">
        <SidebarToolbar v-if="showToolbar" />
      </slot>

      <MaintenanceBanner />
      <AnnouncementBanner
        v-if="authStore.termsAccepted && bannerAnnouncements.length"
        :items="bannerAnnouncements"
        @dismiss="closeAnnouncement"
        @cta="announcementCta"
      />

      <div v-if="showVerifyBanner" class="wb-verify-banner">
        <span class="wb-verify-banner__text">Ověřte svůj e-mail — poslali jsme vám odkaz.</span>
        <button
          type="button"
          class="wb-verify-banner__resend"
          :disabled="resendState === 'sending' || resendState === 'sent'"
          @click="resendVerification"
        >
          <template v-if="resendState === 'sent'">Odesláno ✓</template>
          <template v-else-if="resendState === 'sending'">Odesílám…</template>
          <template v-else-if="resendState === 'error'">Zkusit znovu</template>
          <template v-else>Poslat znovu</template>
        </button>
        <button type="button" class="wb-verify-banner__close" aria-label="Zavřít" @click="dismissVerifyBanner">
          ×
        </button>
      </div>

      <LegalTermsAcceptModal v-if="authStore.user && !authStore.termsAccepted" />
      <AnnouncementModal
        v-if="currentAnnouncement"
        :key="currentAnnouncement.id"
        :item="currentAnnouncement"
        :remaining="modalAnnouncements.length - 1"
        @dismiss="closeAnnouncement(currentAnnouncement)"
        @cta="announcementCta(currentAnnouncement)"
      />
      <FeedbackModal
        v-if="ratingPromptOpen && authStore.termsAccepted && !currentAnnouncement"
        prompted
        @close="ratingPromptOpen = false"
      />
      <FeedbackModal v-if="feedbackOpen" @close="feedbackOpen = false" />
      <!-- „Ukazováček" (useSpotlight): one highlighted element + hint, on demand. -->
      <UiSpotlight />

      <main :style="fullHeight ? 'flex: 1; overflow: hidden; min-height: 0;' : 'flex: 1; overflow: auto;'">
        <div :style="fullHeight ? 'height: 100%;' : 'min-height: 100%;'">
          <slot />
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped>
/* Slim, warm (sand/terracotta) notice — dismissible per session. */
.wb-verify-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: #eef0fa;
  border-bottom: 1px solid #d5dbf2;
  color: #243179;
  font-size: 13px;
}
.wb-verify-banner__text {
  flex: 1;
  min-width: 0;
}
.wb-verify-banner__resend {
  flex: none;
  border: 1px solid #aab4e0;
  background: #fff;
  color: #2b3a8f;
  font-size: 12.5px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
}
.wb-verify-banner__resend:hover:not(:disabled) {
  background: #2b3a8f;
  color: #fff;
}
.wb-verify-banner__resend:disabled {
  opacity: 0.7;
  cursor: default;
}
.wb-verify-banner__close {
  flex: none;
  border: none;
  background: transparent;
  color: #7d8291;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 2px 6px;
}
.wb-verify-banner__close:hover {
  color: #243179;
}
</style>
